import { useCallback, useEffect, useMemo, useRef } from "react";
import { gql, useMutation, useApolloClient } from '@apollo/client';
import { useNavigate } from "react-router";
import {
  useUserContext,
  useUserContextDispatch,
} from "../../../hooks/userContext";
import { OverlayLoader } from "../../../components/overlay-loader";
import { PaymentTokenManager } from "../../../services/paymentApi";
import { Pages } from "../../../utils";

// GraphQL mutation to update onboarding status
const UPDATE_ONBOARDING_STATUS = gql`
  mutation UpdateOnboardingStatus(
    $userId: ID!
    $status: String!
    $accountType: UserAccountType
  ) {
    updateOnboardingStatus(userId: $userId, status: $status, accountType: $accountType)
  }
`;

const UPDATE_ACCOUNT_MEMBERSHIP = gql`
  mutation UpdateAccountMembership(
    $userId: ID!
    $subscription: String
    $billingCycle: String
    $autoRenew: Boolean
  ) {
    updateAccountMembership(
      userId: $userId
      subscription: $subscription
      billingCycle: $billingCycle
      autoRenew: $autoRenew
    ) {
      userId
      subscription
      billingCycle
      autoRenew
    }
  }
`;

interface HomeownerRegistrationCompletionProps {
  homeownerId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  selectedPlan: string;
}

const isMongoObjectId = (value: string) => /^[a-fA-F0-9]{24}$/.test(value);

const resolveSubscriptionFromPaymentData = (paymentData: any): string => {
  if (paymentData?.planType === 'Guild' || paymentData?.planName === 'Guild' || paymentData?.productTier === 'guild') {
    return 'Guild';
  }

  if (paymentData?.planType === 'GuildPlus' || paymentData?.planName === 'Guild+' || paymentData?.productTier === 'guildplus') {
    return 'GuildPlus';
  }

  return 'basic';
};

const resolveSignupBillingCycle = (
  subscription: string,
  paymentData: any
): "monthly" | "annually" | "bi-annually" => {
  const fromPayment = String(paymentData?.billingCycle ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  if (fromPayment.includes("biannual") || fromPayment.includes("semiannual") || fromPayment.includes("6month")) {
    return "bi-annually";
  }

  if (fromPayment.includes("month")) {
    return "monthly";
  }

  if (fromPayment.includes("annual") || fromPayment.includes("year")) {
    return "annually";
  }

  const normalizedSubscription = String(subscription ?? "")
    .trim()
    .toLowerCase();

  if (normalizedSubscription.includes("biannually")) {
    return "bi-annually";
  }

  if (normalizedSubscription.includes("monthly")) {
    return "monthly";
  }

  if (normalizedSubscription === "guildplus" || normalizedSubscription.includes("plus")) {
    return "annually";
  }

  if (normalizedSubscription === "guild") {
    return "monthly";
  }

  return "monthly";
};

const getLegacyPaymentData = () => {
  const paymentDataString = localStorage.getItem('paymentData');
  if (!paymentDataString) {
    return null;
  }

  try {
    return JSON.parse(paymentDataString);
  } catch (error) {
    return null;
  }
};

const getValidUserId = (...candidates: Array<unknown>) => {
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') {
      continue;
    }

    const trimmed = candidate.trim();
    if (!trimmed || trimmed === 'resume-registration' || trimmed === 'undefined' || trimmed === 'null') {
      continue;
    }

    if (isMongoObjectId(trimmed)) {
      return trimmed;
    }
  }

  return null;
};

export const HomeownerRegistrationCompletion = ({
  homeownerId,
  firstName,
  lastName,
  phoneNumber,
  address,
  city,
  state,
  zipCode,
  selectedPlan,
}: HomeownerRegistrationCompletionProps) => {
  const { user } = useUserContext();
  const { updateHomeownerAccountData } = useUserContextDispatch();
  const navigate = useNavigate();
  const [updateAccountMembershipMutation] = useMutation(UPDATE_ACCOUNT_MEMBERSHIP);
  const [updateOnboardingStatusMutation] = useMutation(UPDATE_ONBOARDING_STATUS);
  const apolloClient = useApolloClient();

  const resolvedHomeownerId = useMemo(() => {
    return getValidUserId(
      homeownerId,
      user?.id,
      (user as any)?._id,
      (user as any)?.userId
    );
  }, [homeownerId, user]);

  const handleUpdateAccount = useCallback(async () => {
    const localUserRaw = localStorage.getItem('user');
    let localUser: Record<string, unknown> | null = null;

    if (localUserRaw) {
      try {
        localUser = JSON.parse(localUserRaw);
      } catch (error) {
        localUser = null;
      }
    }

    const effectiveHomeownerId = resolvedHomeownerId ?? getValidUserId(
      localUser?.id,
      localUser?._id,
      localUser?.userId
    );

    try {
      const paymentData = PaymentTokenManager.getPaymentData() || getLegacyPaymentData();
      let finalSubscription = selectedPlan;
      if (!finalSubscription || finalSubscription === '') {
        finalSubscription = resolveSubscriptionFromPaymentData(paymentData);
      }
      const finalBillingCycle = resolveSignupBillingCycle(finalSubscription, paymentData);

      if (!effectiveHomeownerId) {
        console.error('Homeowner registration completion failed: missing valid homeowner ID', {
          homeownerId,
          userId: user?.id,
        });
        sessionStorage.setItem('registrationSuccess', JSON.stringify({
          message: 'We could not finish account linking automatically. Please sign in to complete registration.',
          type: 'info',
          timestamp: Date.now()
        }));
        navigate(Pages.LOGIN, { replace: true });
        return;
      }

      const completionWarnings: string[] = [];

      const updateData: any = {
        _id: effectiveHomeownerId,
        firstName,
        lastName,
        phoneNumber,
        address,
        city,
        state,
        zipCode,
        subscription: finalSubscription,
      };
      
      if (user?.email && user.email !== 'undefined') {
        updateData.email = user.email;
      }

      try {
        await updateHomeownerAccountData(updateData);
      } catch (profileError) {
        completionWarnings.push('profile_update');
        console.error('Failed to update homeowner profile during registration completion', profileError);
      }

      try {
        await updateOnboardingStatusMutation({
          variables: {
            userId: effectiveHomeownerId,
            status: 'active',
            accountType: 'Homeowner'
          }
        });
      } catch (onboardingError) {
        completionWarnings.push('onboarding_status');
        console.error('Failed to mark onboarding status active during registration completion', onboardingError);
      }

      try {
        await updateAccountMembershipMutation({
          variables: {
            userId: effectiveHomeownerId,
            subscription: finalSubscription,
            billingCycle: finalBillingCycle,
            autoRenew: finalSubscription !== 'basic',
          },
        });
      } catch (membershipError: any) {
        completionWarnings.push('membership_status');
        console.error('Failed to persist membership status during registration completion', membershipError);
      }

      localStorage.removeItem('paymentData');
      PaymentTokenManager.clearPaymentData();

      const updatedUser = { 
        ...(user ?? {}),
        id: effectiveHomeownerId,
        onboardingStatus: 'active',
        firstName,
        lastName,
        phoneNumber,
        address,
        city,
        state,
        zipCode,
        subscription: finalSubscription,
        emailVerified: true,
        accountType: 'Homeowner',
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      await new Promise(resolve => setTimeout(resolve, 100));

      sessionStorage.setItem('registrationSuccess', JSON.stringify({
        message: completionWarnings.length
          ? `Welcome to Home Design Guild, ${firstName}! Your account is ready while we finish syncing a few details.`
          : `Welcome to Home Design Guild, ${firstName}! Your account has been created successfully.`,
        type: completionWarnings.length ? 'info' : 'success',
        timestamp: Date.now()
      }));

      try {
        await apolloClient.clearStore();
      } catch (cacheError) {
        console.error('Failed to clear Apollo cache during registration completion', cacheError);
      }

      navigate(Pages.YOUR_HUB, { replace: true });
    } catch (error: any) {
      console.error('Unexpected error during homeowner registration completion', error);
      sessionStorage.setItem('registrationSuccess', JSON.stringify({
        message: `Welcome to Home Design Guild, ${firstName}! Redirecting you to your account now.`,
        type: 'info',
        timestamp: Date.now()
      }));
      navigate(Pages.YOUR_HUB, { replace: true });
    }
  }, [
    resolvedHomeownerId,
    homeownerId,
    firstName,
    lastName,
    phoneNumber,
    address,
    city,
    state,
    zipCode,
    selectedPlan,
    updateHomeownerAccountData,
    updateAccountMembershipMutation,
    updateOnboardingStatusMutation,
    user,
    apolloClient,
    navigate,
  ]);

  const hasTriggered = useRef(false);
  useEffect(() => {
    if (hasTriggered.current) {
      return;
    }

    const hasCandidateId = Boolean(
      homeownerId ||
      user?.id ||
      (user as any)?._id ||
      (user as any)?.userId
    );

    if (!hasCandidateId) {
      return;
    }

    hasTriggered.current = true;
    void handleUpdateAccount();
  }, [homeownerId, user, handleUpdateAccount]);

  // Let the registration page handle navigation after user context is updated

  return <OverlayLoader loadingText="Completing Your Registration" />;
};
